/*
 * WBAB mystery scratch card — a visual layer over the Klaviyo popup.
 *
 * Klaviyo keeps doing everything real (signup, list, unique coupon, analytics,
 * flows). This script only listens to Klaviyo's documented `klaviyoForms`
 * window events, finds two anchors in the form, and draws canvases on top:
 *   - email step: a foil "mystery discount" card over the text block whose
 *     whole text is the configured cardText
 *   - success step: a scratch-off cover over the "$15 OFF" text block plus the
 *     coupon block below it
 *   - scratch-first variation: a scratch-off cover over the text block whose
 *     whole text is the configured prizeText (step 1, before the email step).
 *     Revealing it there means the success step later just shows the code.
 * The layers live in their own fixed container on <body>, never inside
 * Klaviyo's DOM, so the form's markup is never changed. The code stays in the
 * form's DOM the whole time (screen readers read it), and if anything here
 * fails the form simply shows the code as usual.
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('wbab-scratch-config');
  if (!cfgEl || window.__wbabScratch || !window.requestAnimationFrame) return;
  window.__wbabScratch = true;

  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  var amountRe = new RegExp(cfg.amountPattern || '\\$\\s*15\\s*off', 'i');
  var cardText = norm(cfg.cardText || 'mystery discount');
  var prizeText = norm(cfg.prizeText || '');
  var threshold = cfg.threshold || 0.55;
  var accent = cfg.accent || '#d0473e';
  var STORE_KEY = 'wbabScratchRevealed:' + cfg.formId;

  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  var DPR = Math.min(window.devicePixelRatio || 1, 2); // capped for low-end phones

  function reduced() { return !!(mq && mq.matches); }
  function norm(s) { return String(s || '').replace(/\s+/g, ' ').trim().toLowerCase(); }
  function revealedBefore() { try { return sessionStorage.getItem(STORE_KEY) === '1'; } catch (e) { return false; } }
  function markRevealed() { try { sessionStorage.setItem(STORE_KEY, '1'); } catch (e) {} }

  function fontStack(varName) {
    var v = '';
    try { v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim(); } catch (e) {}
    return v || '"DM Sans", "Helvetica Neue", Arial, sans-serif';
  }
  var HEAD_FONT = fontStack('--heading-font');
  var BODY_FONT = fontStack('--body-font');

  /* ---------- styles + host container ---------- */

  var host = null;
  function getHost() {
    if (host && host.isConnected) return host;
    if (!document.getElementById('wbab-scratch-css')) {
      var st = document.createElement('style');
      st.id = 'wbab-scratch-css';
      st.textContent =
        '.wbab-sc-host{position:fixed;left:0;top:0;width:0;height:0;overflow:visible;z-index:2147483000;pointer-events:none}' +
        '.wbab-sc-layer{position:absolute;left:0;top:0;pointer-events:none;will-change:transform}' +
        '.wbab-sc-cover{border-radius:6px;overflow:hidden;box-shadow:0 8px 22px rgba(0,0,0,.28);transition:opacity .45s ease}' +
        '.wbab-sc-cover canvas{display:block;width:100%;height:100%;touch-action:none;pointer-events:auto;cursor:grab;-webkit-user-select:none;user-select:none;-webkit-touch-callout:none}' +
        '.wbab-sc-cover canvas:active{cursor:grabbing}' +
        '.wbab-sc-cover.is-gone{opacity:0}' +
        '.wbab-sc-cover.is-gone canvas{transform:scale(1.04);transition:transform .45s ease}' +
        '.wbab-sc-card{border-radius:8px;overflow:hidden;box-shadow:0 8px 22px rgba(0,0,0,.22)}' +
        '.wbab-sc-card canvas{display:block;width:100%;height:100%}' +
        '.wbab-sc-btn{pointer-events:auto;position:absolute;font:600 11px/1 ' + BODY_FONT + ';letter-spacing:.12em;text-transform:uppercase;border-radius:30px;cursor:pointer;-webkit-tap-highlight-color:transparent}' +
        '.wbab-sc-tap{left:50%;bottom:7px;transform:translateX(-50%);color:#f1d48a;background:rgba(0,0,0,.45);border:1px solid rgba(241,212,138,.6);padding:7px 12px;white-space:nowrap}' +
        '.wbab-sc-tap:focus-visible,.wbab-sc-copy:focus-visible{outline:2px solid ' + accent + ';outline-offset:2px}' +
        '.wbab-sc-copy{left:0;top:0;transform:translateX(-50%);color:#fff;background:#111;border:0;padding:10px 18px;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,.18)}' +
        '.wbab-sc-copy:hover{background:#000}' +
        '.wbab-sc-live{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)}';
      document.head.appendChild(st);
    }
    host = document.createElement('div');
    host.className = 'wbab-sc-host';
    // The host sits outside Klaviyo's DOM, so on mobile Klaviyo treats a tap on
    // the foil or button as a tap outside the popup and closes it.
    ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'click'].forEach(function (t) {
      host.addEventListener(t, function (e) { e.stopPropagation(); });
    });
    document.body.appendChild(host);
    // Canvas text can't wait for the web font, so redraw once it's loaded.
    if (document.fonts && document.fonts.load) {
      document.fonts.load('600 16px ' + HEAD_FONT).then(function () {
        overlays.forEach(function (o) { o.last.key = ''; o.last.w = -1; });
      }, function () {});
    }
    return host;
  }

  /* ---------- overlay tracking: one rAF loop keeps layers glued to anchors ---------- */

  var overlays = [];
  var loopId = 0;
  function track(o) {
    // Place it now so it never paints a frame at the corner of the screen.
    var ok = false;
    try { ok = o.update(); } catch (e) {}
    if (!ok) { o.destroy(); return; }
    overlays.push(o);
    if (!loopId) loopId = requestAnimationFrame(loop);
  }
  function loop() {
    loopId = 0;
    for (var i = overlays.length - 1; i >= 0; i--) {
      var o = overlays[i], ok = false;
      try { ok = o.update(); } catch (e) { ok = false; }
      if (!ok) { o.destroy(); overlays.splice(i, 1); }
    }
    if (overlays.length) loopId = requestAnimationFrame(loop);
  }
  function teardown() {
    overlays.forEach(function (o) { o.destroy(); });
    overlays = [];
    mounted.clear(); // a reopened popup gets its overlays again
  }

  // While Klaviyo animates the popup open (or a step is briefly hidden) a
  // block can measure 0x0. Keep the overlay and just hide it until it has a
  // size again; only drop it once Klaviyo has removed the block for good.
  function waitFor(layer, els) {
    for (var i = 0; i < els.length; i++) if (!els[i] || !els[i].isConnected) return false;
    layer.style.visibility = 'hidden';
    return true;
  }
  function rectOf(el) {
    if (!el || !el.isConnected) return null;
    var r = el.getBoundingClientRect();
    return r.width > 2 && r.height > 2 ? r : null;
  }
  function union(a, b, pad) {
    var l = Math.min(a.left, b.left) - pad, t = Math.min(a.top, b.top) - pad;
    var r = Math.max(a.right, b.right) + pad, btm = Math.max(a.bottom, b.bottom) + pad;
    return { left: l, top: t, width: r - l, height: btm - t };
  }
  // Place a layer at rect r and clip it to the popup's box so it can't float
  // over the page when the popup scrolls on small screens.
  function place(layer, r, clipEl, last) {
    var key = Math.round(r.left) + ',' + Math.round(r.top) + ',' + Math.round(r.width) + ',' + Math.round(r.height);
    var c = clipEl && clipEl.getBoundingClientRect();
    if (c && (c.width < 2 || c.height < 2)) c = null;
    var clip = c ? [Math.max(0, c.top - r.top), Math.max(0, r.left + r.width - c.right), Math.max(0, r.top + r.height - c.bottom), Math.max(0, c.left - r.left)] : null;
    var ck = clip ? clip.map(Math.round).join(' ') : '';
    if (last.key === key && last.clip === ck) return false;
    var sized = last.w !== Math.round(r.width) || last.h !== Math.round(r.height);
    layer.style.transform = 'translate(' + r.left + 'px,' + r.top + 'px)';
    layer.style.width = r.width + 'px';
    layer.style.height = r.height + 'px';
    layer.style.clipPath = clip && ck !== '0 0 0 0' ? 'inset(' + clip.join('px ') + 'px)' : '';
    last.key = key; last.clip = ck; last.w = Math.round(r.width); last.h = Math.round(r.height);
    return sized;
  }

  // Klaviyo scrolls the popup inside its role=dialog wrapper, not the <form>.
  function scroller(form) {
    for (var p = form; p && p !== document.body; p = p.parentElement) {
      var o = getComputedStyle(p).overflowY;
      if (o === 'auto' || o === 'scroll' || o === 'hidden') return p;
    }
    return null;
  }

  /* ---------- canvas foil: brushed steel, gold edge, engraved type ---------- */

  function lcg(seed) { return function () { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; }; }

  function goldGradient(ctx, x0, x1) {
    var g = ctx.createLinearGradient(x0, 0, x1, 0);
    g.addColorStop(0, '#9c7a36'); g.addColorStop(0.35, '#f1d48a'); g.addColorStop(0.55, '#c9a24f');
    g.addColorStop(0.8, '#f6e2a8'); g.addColorStop(1, '#9c7a36');
    return g;
  }

  // Renders the static foil into an offscreen canvas once per size.
  function renderFoil(w, h, lines) {
    var c = document.createElement('canvas');
    c.width = Math.round(w * DPR); c.height = Math.round(h * DPR);
    var x = c.getContext('2d');
    x.setTransform(DPR, 0, 0, DPR, 0, 0);

    var g = x.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#1a1c1f'); g.addColorStop(0.42, '#3a3f45'); g.addColorStop(0.5, '#50565d');
    g.addColorStop(0.58, '#3a3f45'); g.addColorStop(1, '#141618');
    x.fillStyle = g; x.fillRect(0, 0, w, h);

    var rnd = lcg(7);
    for (var y = 0; y < h; y += 0.75) { // fine horizontal brushing
      var a = rnd() * 0.07;
      x.fillStyle = rnd() > 0.5 ? 'rgba(255,255,255,' + a.toFixed(3) + ')' : 'rgba(0,0,0,' + (a * 1.4).toFixed(3) + ')';
      x.fillRect(0, y, w, 0.5);
    }
    for (var i = 0; i < 90; i++) { // longer streaks
      x.fillStyle = 'rgba(255,255,255,' + (0.03 + rnd() * 0.06).toFixed(3) + ')';
      x.fillRect(rnd() * w - 40, rnd() * h, 40 + rnd() * w * 0.5, 0.6);
    }

    x.strokeStyle = goldGradient(x, 0, w); // gold bezel
    x.lineWidth = 1.5; x.strokeRect(3.5, 3.5, w - 7, h - 7);
    x.globalAlpha = 0.35; x.lineWidth = 0.75; x.strokeRect(7, 7, w - 14, h - 14);
    x.globalAlpha = 1;

    x.textAlign = 'center'; x.textBaseline = 'middle';
    var n = lines.length, gap = h / (n + 1);
    lines.forEach(function (ln, i) {
      var size = Math.max(10, Math.min(ln.size * h, ln.max));
      x.font = (ln.weight || 600) + ' ' + size + 'px ' + (ln.font || HEAD_FONT);
      if ('letterSpacing' in x) x.letterSpacing = (ln.track || 0) * size + 'px';
      var ty = ln.y != null ? ln.y * h : gap * (i + 1);
      x.fillStyle = 'rgba(0,0,0,.55)'; x.fillText(ln.text, w / 2, ty + 1); // engraved shadow
      x.fillStyle = ln.gold ? goldGradient(x, w * 0.2, w * 0.8) : 'rgba(255,255,255,.72)';
      x.fillText(ln.text, w / 2, ty);
    });
    if ('letterSpacing' in x) x.letterSpacing = '0px';
    return c;
  }

  // Draw foil + an optional diagonal sheen band at position p (0..1).
  function paint(ctx, foil, w, h, p) {
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(foil, 0, 0, w, h);
    if (p == null || p <= 0 || p >= 1) return;
    var cx = -w * 0.3 + p * w * 1.6;
    var g = ctx.createLinearGradient(cx - 70, 0, cx + 70, h * 0.6);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.5, 'rgba(255,245,220,.22)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
  }

  // Sheen sweeps every few seconds; returns a stop function.
  function shimmer(draw) {
    if (reduced()) { draw(null); return function () {}; }
    var id = 0, t0 = performance.now(), stopped = false;
    function f(t) {
      if (stopped) return;
      var cycle = ((t - t0) % 3600) / 1100; // 1.1 s sweep, 2.5 s rest
      draw(cycle < 1 ? cycle : null);
      id = requestAnimationFrame(f);
    }
    id = requestAnimationFrame(f);
    return function () { stopped = true; cancelAnimationFrame(id); };
  }

  /* ---------- email step: decorative mystery card ---------- */

  function Card(form, el) {
    this.form = form; this.el = el; this.last = {}; this.clip = scroller(form);
    this.layer = document.createElement('div');
    this.layer.className = 'wbab-sc-layer wbab-sc-card';
    this.layer.setAttribute('aria-hidden', 'true');
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.layer.appendChild(this.canvas);
    getHost().appendChild(this.layer);
    this.stop = null;
  }
  Card.prototype.update = function () {
    var r = rectOf(this.el);
    if (!r) return waitFor(this.layer, [this.el]);
    this.layer.style.visibility = '';
    if (place(this.layer, r, this.clip, this.last)) this.draw(r.width, r.height);
    return true;
  };
  Card.prototype.draw = function (w, h) {
    var self = this;
    this.canvas.width = Math.round(w * DPR); this.canvas.height = Math.round(h * DPR);
    var foil = renderFoil(w, h, [
      { text: 'YOUR', size: 0.11, max: 12, track: 0.35, y: 0.24, font: BODY_FONT, weight: 500 },
      { text: 'MYSTERY DISCOUNT', size: 0.2, max: 26, track: 0.12, y: 0.47, gold: true },
      { text: 'Revealed the moment you join', size: 0.12, max: 13, y: 0.72, font: BODY_FONT, weight: 400 }
    ]);
    if (this.stop) this.stop();
    this.stop = shimmer(function (p) { paint(self.ctx, foil, w, h, p); });
  };
  Card.prototype.destroy = function () {
    if (this.stop) this.stop();
    this.layer.remove();
    mounted.delete(this.el);
  };

  /* ---------- success step: the scratch cover ---------- */

  var GRID_X = 24, GRID_Y = 10;

  function Scratch(form, amountEl, couponBox, codeEl) {
    this.form = form; this.amountEl = amountEl; this.couponBox = couponBox; this.codeEl = codeEl; this.clip = scroller(form);
    this.last = {}; this.strokes = []; this.cells = new Uint8Array(GRID_X * GRID_Y); this.hit = 0;
    this.started = false; this.done = false; this.w = 0; this.h = 0; this.stop = null;

    var L = this.layer = document.createElement('div');
    L.className = 'wbab-sc-layer wbab-sc-cover';
    var c = this.canvas = document.createElement('canvas');
    c.setAttribute('aria-hidden', 'true');
    this.ctx = c.getContext('2d');
    L.appendChild(c);

    var b = this.tapBtn = document.createElement('button');
    b.type = 'button';
    b.className = 'wbab-sc-btn wbab-sc-tap';
    b.textContent = reduced() ? 'Tap to reveal' : 'or tap to reveal';
    L.appendChild(b);
    getHost().appendChild(L);

    var self = this;
    b.addEventListener('click', function (e) { e.preventDefault(); self.reveal(false); });
    this.bindPointer();
    // Keyboard users can't scratch, and Klaviyo traps focus inside the
    // popup, so any keyboard navigation while covered simply reveals.
    this.onKey = function (e) {
      if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ') self.reveal(false);
    };
    document.addEventListener('keydown', this.onKey, true);
  }
  // Without a coupon (scratch-first variation) the cover is just the prize block.
  Scratch.prototype.rect = function () {
    var a = rectOf(this.amountEl);
    if (!this.couponBox) return a;
    var c = rectOf(this.couponBox);
    return a && c ? union(a, c, 4) : null;
  };
  Scratch.prototype.update = function () {
    if (this.done) return false;
    var r = this.rect();
    if (!r) return waitFor(this.layer, this.couponBox ? [this.amountEl, this.couponBox] : [this.amountEl]);
    this.layer.style.visibility = '';
    if (place(this.layer, r, this.clip, this.last)) this.draw(r.width, r.height);
    return true;
  };
  Scratch.prototype.brush = function () { return Math.max(20, Math.min(this.h * 0.26, 40)); };
  Scratch.prototype.draw = function (w, h) {
    var self = this;
    this.w = w; this.h = h;
    this.canvas.width = Math.round(w * DPR); this.canvas.height = Math.round(h * DPR);
    this.foil = renderFoil(w, h, this.couponBox ? [
      { text: 'SCRATCH TO REVEAL', size: 0.15, max: 22, track: 0.14, y: 0.36, gold: true },
      { text: coarse ? 'Use your finger' : 'Click and drag to scratch', size: 0.1, max: 13, y: 0.56, font: BODY_FONT, weight: 400 }
    ] : [
      { text: 'SCRATCH TO REVEAL YOUR GIFT', size: 0.15, max: 22, track: 0.12, y: 0.36, gold: true },
      { text: coarse ? 'Use your finger' : 'Click and drag to scratch', size: 0.1, max: 13, y: 0.56, font: BODY_FONT, weight: 400 }
    ]);
    if (this.stop) this.stop();
    if (this.started) {
      paint(this.ctx, this.foil, w, h, null);
      for (var i = 0; i < this.strokes.length; i++) this.erase(this.strokes[i], false);
    } else {
      this.stop = shimmer(function (p) { paint(self.ctx, self.foil, w, h, p); });
    }
  };
  // Strokes are stored normalized (0..1) so a resize can replay them.
  Scratch.prototype.erase = function (s, count) {
    var ctx = this.ctx, w = this.w, h = this.h, r = this.brush();
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = r;
    ctx.beginPath();
    ctx.moveTo(s[0] * w, s[1] * h);
    ctx.lineTo(s[2] * w + 0.01, s[3] * h);
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
    if (count) this.mark(s, r / 2);
  };
  // Coverage is tracked on a coarse grid rather than with getImageData, which
  // stalls the GPU on low-end phones.
  Scratch.prototype.mark = function (s, rad) {
    var w = this.w, h = this.h;
    var x0 = s[0] * w, y0 = s[1] * h, x1 = s[2] * w, y1 = s[3] * h;
    var len = Math.hypot(x1 - x0, y1 - y0), steps = Math.max(1, Math.ceil(len / (rad / 2)));
    var cw = w / GRID_X, ch = h / GRID_Y;
    for (var k = 0; k <= steps; k++) {
      var px = x0 + (x1 - x0) * k / steps, py = y0 + (y1 - y0) * k / steps;
      var gx0 = Math.max(0, Math.floor((px - rad) / cw)), gx1 = Math.min(GRID_X - 1, Math.floor((px + rad) / cw));
      var gy0 = Math.max(0, Math.floor((py - rad) / ch)), gy1 = Math.min(GRID_Y - 1, Math.floor((py + rad) / ch));
      for (var gy = gy0; gy <= gy1; gy++) for (var gx = gx0; gx <= gx1; gx++) {
        var i = gy * GRID_X + gx;
        if (this.cells[i]) continue;
        var dx = (gx + 0.5) * cw - px, dy = (gy + 0.5) * ch - py;
        if (dx * dx + dy * dy <= rad * rad) { this.cells[i] = 1; this.hit++; }
      }
    }
    if (this.hit / this.cells.length >= threshold) this.reveal(true);
  };
  Scratch.prototype.bindPointer = function () {
    var self = this, c = this.canvas, down = false, lx = 0, ly = 0;
    function pt(e) {
      var b = c.getBoundingClientRect();
      return [(e.clientX - b.left) / b.width, (e.clientY - b.top) / b.height];
    }
    function seg(p) {
      var s = [lx, ly, p[0], p[1]];
      self.strokes.push(s); self.erase(s, true);
      lx = p[0]; ly = p[1];
    }
    c.addEventListener('pointerdown', function (e) {
      if (self.done) return;
      e.preventDefault();
      try { c.setPointerCapture(e.pointerId); } catch (x) {}
      down = true;
      if (!self.started) {
        self.started = true;
        if (self.stop) { self.stop(); self.stop = null; }
        paint(self.ctx, self.foil, self.w, self.h, null);
        self.tapBtn.style.display = 'none';
      }
      var p = pt(e); lx = p[0]; ly = p[1]; seg(p);
    });
    c.addEventListener('pointermove', function (e) {
      if (!down || self.done) return;
      e.preventDefault();
      var evs = e.getCoalescedEvents ? e.getCoalescedEvents() : null;
      if (evs && evs.length) for (var i = 0; i < evs.length && !self.done; i++) seg(pt(evs[i]));
      else seg(pt(e));
    });
    function up() { down = false; }
    c.addEventListener('pointerup', up);
    c.addEventListener('pointercancel', up);
    // Belt and braces for older iOS: never let a scratch scroll the page.
    c.addEventListener('touchmove', function (e) { e.preventDefault(); }, { passive: false });
    c.addEventListener('contextmenu', function (e) { e.preventDefault(); });
  };
  Scratch.prototype.reveal = function (celebrate) {
    if (this.done) return;
    this.done = true;
    markRevealed();
    if (this.stop) this.stop();
    document.removeEventListener('keydown', this.onKey, true);
    var r = this.rect(), L = this.layer;
    L.style.pointerEvents = 'none';
    this.canvas.style.pointerEvents = 'none';
    this.tapBtn.remove();
    if (reduced()) L.remove();
    else {
      L.classList.add('is-gone');
      setTimeout(function () { L.remove(); }, 500);
      if (celebrate && r) confetti(r);
    }
    try { var sel = window.getSelection(); if (sel) sel.removeAllRanges(); } catch (e) {}
    if (this.couponBox) {
      announce('Your code is revealed: ' + codeText(this.codeEl));
      mountCopy(this.form, this.couponBox, this.codeEl);
    } else {
      announce('You unlocked ' + this.amountEl.textContent.replace(/\s+/g, ' ').trim() + '. Claim it below.');
    }
  };
  Scratch.prototype.destroy = function () {
    if (this.stop) this.stop();
    document.removeEventListener('keydown', this.onKey, true);
    if (!this.done) this.layer.remove();
    mounted.delete(this.amountEl);
  };

  /* ---------- after the reveal: copy button + tap-code-to-copy ---------- */

  function codeText(el) { return (el && el.textContent || '').replace(/\s+/g, '').trim(); }

  function copy(text, done) {
    function legacy() {
      var t = document.createElement('textarea');
      t.value = text; t.setAttribute('readonly', '');
      t.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      document.body.appendChild(t); t.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) {}
      t.remove(); done(ok);
    }
    if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(text).then(function () { done(true); }, legacy);
    else legacy();
  }

  function Copy(form, couponBox, codeEl) {
    this.form = form; this.box = couponBox; this.codeEl = codeEl; this.last = {}; this.free = null;
    var L = this.layer = document.createElement('div');
    L.className = 'wbab-sc-layer';
    var b = this.btn = document.createElement('button');
    b.type = 'button';
    b.className = 'wbab-sc-btn wbab-sc-copy';
    b.textContent = 'Copy code';
    L.appendChild(b);
    getHost().appendChild(L);
    var self = this;
    this.born = Date.now();
    this.onCopy = function (e) {
      // The pointerup that finishes a scratch lands on the code as a click.
      if (e && e.currentTarget === couponBox && Date.now() - self.born < 600) return;
      if (e) e.preventDefault();
      var code = codeText(self.codeEl);
      if (!code) return;
      copy(code, function (ok) {
        b.textContent = ok ? 'Copied ✓' : 'Press and hold the code to copy';
        announce(ok ? 'Code copied' : '');
        clearTimeout(self.t);
        self.t = setTimeout(function () { b.textContent = 'Copy code'; }, 2200);
      });
    };
    b.addEventListener('click', this.onCopy);
    couponBox.addEventListener('click', this.onCopy);
    couponBox.style.cursor = 'copy';
  }
  Copy.prototype.update = function () {
    var r = rectOf(this.box);
    if (!r) return waitFor(this.layer, [this.box]);
    this.layer.style.visibility = '';
    var at = { left: r.left + r.width / 2, top: r.bottom + 10, width: 1, height: 1 };
    place(this.layer, at, null, this.last);
    // Only show the chip if the space under the coupon is empty (the Klaviyo
    // spacer block). Otherwise tapping the code itself still copies.
    if (this.free === null && this.btn.offsetHeight) {
      var y = at.top + this.btn.offsetHeight / 2;
      host.style.display = 'none';
      var under = document.elementFromPoint(at.left, y);
      host.style.display = '';
      this.free = !!under && this.form.contains(under) && !isContent(under);
      this.btn.style.visibility = this.free ? 'visible' : 'hidden';
    }
    return true;
  };
  Copy.prototype.destroy = function () {
    clearTimeout(this.t);
    this.box.removeEventListener('click', this.onCopy);
    this.layer.remove();
  };
  function isContent(el) {
    if (/^(BUTTON|A|INPUT|SELECT|TEXTAREA|IMG|LABEL)$/.test(el.tagName)) return true;
    return norm(el.innerText).length > 0;
  }

  var copyMounted = new WeakSet();
  function mountCopy(form, couponBox, codeEl) {
    if (copyMounted.has(couponBox)) return;
    copyMounted.add(couponBox);
    var o = new Copy(form, couponBox, codeEl);
    o.btn.style.visibility = 'hidden';
    var d = o.destroy;
    o.destroy = function () { d.call(o); copyMounted.delete(couponBox); };
    track(o);
  }

  /* ---------- confetti (small, brief, gold) ---------- */

  function confetti(r) {
    var pad = 70, w = r.width + pad * 2, h = r.height + pad * 2;
    var c = document.createElement('canvas');
    c.setAttribute('aria-hidden', 'true');
    c.width = Math.round(w * DPR); c.height = Math.round(h * DPR);
    c.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;width:' + w + 'px;height:' + h + 'px;transform:translate(' + (r.left - pad) + 'px,' + (r.top - pad) + 'px)';
    getHost().appendChild(c);
    var x = c.getContext('2d'), rnd = Math.random;
    var colors = ['#f1d48a', '#c9a24f', '#ffffff', '#e8e8e8', accent];
    var ps = [];
    for (var i = 0; i < 46; i++) {
      var ang = -Math.PI / 2 + (rnd() - 0.5) * Math.PI * 1.1, sp = 3 + rnd() * 5;
      ps.push({ x: w / 2 + (rnd() - 0.5) * r.width * 0.6, y: pad + r.height / 2, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp,
        rot: rnd() * 6, vr: (rnd() - 0.5) * 0.4, s: 3 + rnd() * 4, c: colors[i % colors.length] });
    }
    var t0 = performance.now();
    (function f(t) {
      var k = (t - t0) / 1100;
      x.setTransform(DPR, 0, 0, DPR, 0, 0);
      x.clearRect(0, 0, w, h);
      if (k >= 1) { c.remove(); return; }
      x.globalAlpha = 1 - k * k;
      ps.forEach(function (p) {
        p.vy += 0.18; p.vx *= 0.985; p.x += p.vx; p.y += p.vy; p.rot += p.vr;
        x.save(); x.translate(p.x, p.y); x.rotate(p.rot);
        x.fillStyle = p.c; x.fillRect(-p.s / 2, -p.s / 4, p.s, p.s / 2);
        x.restore();
      });
      requestAnimationFrame(f);
    })(t0);
  }

  var live = null;
  function announce(msg) {
    if (!msg) return;
    if (!live || !live.isConnected) {
      live = document.createElement('div');
      live.className = 'wbab-sc-live';
      live.setAttribute('aria-live', 'polite');
      getHost().appendChild(live);
    }
    live.textContent = msg;
  }

  /* ---------- finding anchors in Klaviyo's form ---------- */

  function findForms() {
    var id = cfg.formId;
    var list = document.querySelectorAll('form[data-testid="klaviyo-form-' + id + '"], .klaviyo-form-' + id + ' form');
    return Array.prototype.slice.call(list);
  }

  // Deepest block-level element whose full text passes test().
  function findText(form, test) {
    var all = form.querySelectorAll('div,p,h1,h2,h3,h4,h5,h6,span');
    var best = null;
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (best && !best.contains(el)) continue;
      if (test(el.textContent)) best = el;
    }
    while (best && best !== form && getComputedStyle(best).display === 'inline') best = best.parentElement;
    return best && best !== form ? best : null;
  }

  // Coupon block: an explicit `.wbab-scratch` class if set in Klaviyo, else
  // the first monospace text after the amount line (Klaviyo's coupon block).
  function findCoupon(form, after) {
    var codeEl = null, marked = form.querySelector('.wbab-scratch');
    var scope = marked || form;
    var leaves = scope.querySelectorAll('*');
    for (var i = 0; i < leaves.length; i++) {
      var el = leaves[i];
      if (el.childElementCount || !codeText(el)) continue;
      if (!marked && !(after.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
      if (after.contains(el)) continue;
      if (marked || /courier|mono/i.test(getComputedStyle(el).fontFamily)) { codeEl = el; break; }
    }
    if (!codeEl) return null;
    var box = marked || codeEl;
    if (!marked) {
      for (var p = codeEl, k = 0; p && p !== form && k < 5; p = p.parentElement, k++) {
        var cs = getComputedStyle(p);
        if (cs.borderTopStyle !== 'none' && parseFloat(cs.borderTopWidth) > 0) { box = p; break; }
      }
    }
    return { box: box, code: codeEl };
  }

  var mounted = new Set();

  function scan() {
    var forms = findForms();
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];

      var cardEl = findText(form, function (t) { return norm(t) === cardText; });
      // Cover the whole padded Klaviyo block, not just the text line inside it.
      var cardBlock = cardEl && cardEl.closest('[data-testid="form-component"]');
      if (cardBlock && form.contains(cardBlock) && norm(cardBlock.textContent) === cardText) cardEl = cardBlock;
      if (cardEl && !mounted.has(cardEl)) { mounted.add(cardEl); track(new Card(form, cardEl)); }

      // Scratch-first variation: scratch the prize before the email step.
      if (prizeText) {
        var prizeEl = findText(form, function (t) { return norm(t) === prizeText; });
        var prizeBlock = prizeEl && prizeEl.closest('[data-testid="form-component"]');
        if (prizeBlock && form.contains(prizeBlock) && norm(prizeBlock.textContent) === prizeText) prizeEl = prizeBlock;
        if (prizeEl && !mounted.has(prizeEl)) {
          mounted.add(prizeEl);
          if (!revealedBefore()) track(new Scratch(form, prizeEl, null, null));
        }
        if (prizeEl) continue; // no coupon on this step
      }

      var amountEl = findText(form, function (t) { return amountRe.test(t) && norm(t).length < 60; });
      if (!amountEl || mounted.has(amountEl)) continue;
      var coupon = findCoupon(form, amountEl);
      if (!coupon) continue; // code not rendered yet; the observer retries
      mounted.add(amountEl);
      if (revealedBefore()) mountCopy(form, coupon.box, coupon.code); // don't make them scratch twice
      else track(new Scratch(form, amountEl, coupon.box, coupon.code));
    }
  }

  /* ---------- wiring to Klaviyo's form events ---------- */

  var mo = null, until = 0, queued = false, stopTimer = 0;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      try { scan(); } catch (e) { /* never break the page; the form stays as-is */ }
    });
  }
  function watch(ms) {
    until = Math.max(until, Date.now() + ms);
    if (!mo) {
      mo = new MutationObserver(schedule);
      mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
    clearTimeout(stopTimer);
    stopTimer = setTimeout(function () { if (mo) { mo.disconnect(); mo = null; } }, until - Date.now());
    schedule();
  }

  window.addEventListener('klaviyoForms', function (e) {
    var d = e.detail || {};
    if ((d.formId || d.formID) !== cfg.formId) return;
    if (d.type === 'close') { teardown(); return; }
    if (d.type === 'open' || d.type === 'embedOpen') watch(8000);
    else if (d.type === 'stepSubmit' || d.type === 'submit') watch(20000); // coupon renders after the API call
  });
})();
